import { getDomainID, qaBackendApiInstance } from '../config/config'
import { getVideoPreview } from 'polotno/utils/video'
import { useMutation, useQuery } from 'react-query'

export async function getImagesFromWebsites(url: string) {
  // Validate and normalize URL
  if (!url || url.trim() === '') {
    throw new Error('URL is required');
  }

  let normalizedUrl = url.trim();
  
  // Add protocol if missing
  if (!normalizedUrl.startsWith('http://') && !normalizedUrl.startsWith('https://')) {
    normalizedUrl = 'https://' + normalizedUrl;
  }

  // Validate URL format
  try {
    new URL(normalizedUrl);
  } catch (error) {
    throw new Error('Invalid URL format');
  }

  // Check if URL contains valid domain
  const urlPattern = /^https?:\/\/([\w-]+\.)+[\w-]+(\/.*)?$/;
  if (!urlPattern.test(normalizedUrl)) {
    throw new Error('Invalid domain format');
  }

  const endpoint = `/project/gen-ai-data-images-v2?url=${encodeURIComponent(normalizedUrl)}`;
  const response = await qaBackendApiInstance.post(endpoint);
  const data = response.data;
  
  // Handle response - assuming data is a direct array of images
  const images = Array.isArray(data) ? data : (data?.images || []);
  
  // Filter out invalid or non-image URLs
  const filteredImages = images.filter((image: any) => {
    const imageUrl = image.url || image.src || image.image_url || image;
    if (typeof imageUrl !== 'string') return false;
    return imageUrl && (
      imageUrl.includes('.jpg') || 
      imageUrl.includes('.jpeg') || 
      imageUrl.includes('.png') || 
      imageUrl.includes('.gif') || 
      imageUrl.includes('.webp') ||
      imageUrl.includes('.svg')
    );
  });
  
  // Validate images by checking if they can actually load
  const validImages = await Promise.all(
    filteredImages?.map(
      (image: any) =>
        new Promise((resolve) => {
          const imageUrl = image.url || image.src || image.image_url || image;
          const img = new window.Image();
          img.src = imageUrl;
          img.onload = () => resolve(image);
          img.onerror = () => resolve(null);
        })
    )
  );
  
  // Filter out null values (failed to load images)
  const loadableImages = validImages.filter(image => image !== null);
  
  return { 
    images: loadableImages,
    originalUrl: normalizedUrl // Include the normalized URL for display
  };
}

export function useGetImagesFromWebsites(url: string) {
  return useQuery(['images-from-websites', url], () => getImagesFromWebsites(url), {
    enabled: !!url,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// https://dev-quickads.brandbooster.ai/api/v1/project/gen-ai-data-images-v2?url=nike.com
// https://dev-quickads.brandbooster.ai/api/v1/project/gen-ai-data-images-v2?url=nike.com